import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Dropdown, Label} from 'platform/components';
import {Box, Center, Spinner, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useEffect, useMemo, useRef, useState, type ReactNode} from 'react';

import {isNil, sortBy, trim} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {useReorderRelativeFiltersMutation} from '../../../../../store/omneticApi';
import {RelativeFilterResponseBody} from '../../../../../types/api/api';
import {handleApiError} from '../../../../../utils/handleApiError';
import {buildReorderRelativeFiltersBody} from '../utils/buildReorderRelativeFiltersBody';
import {filterPresetsBySearch} from '../utils/filterPresetsBySearch';
import {reorderPresetsInList} from '../utils/reorderPresetsInList';
import {PresetListItem} from './PresetListItem';
import {SavedFiltersDropdownFooter} from './SavedFiltersDropdownFooter';
import {SavedFiltersDropdownSearch} from './SavedFiltersDropdownSearch';
import {SavedFiltersDropdownTrigger} from './SavedFiltersDropdownTrigger';
import {SortablePresetListItem} from './SortablePresetListItem';

interface SavedFiltersDropdownProps extends TestIdProps {
  label: string;
  presets: RelativeFilterResponseBody[];
  selectedPresetId: string | null;
  placeholder: string;
  customerId: string | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onSelectPreset: (presetId: string) => void;
  onCreatePreset: () => void;
}

export function SavedFiltersDropdown(props: SavedFiltersDropdownProps) {
  const {
    label,
    presets,
    selectedPresetId,
    placeholder,
    customerId,
    isLoading,
    isError,
    onSelectPreset,
    onCreatePreset,
  } = props;

  const [isOpen, openDropdown, closeDropdown] = useBoolean();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [reorderRelativeFilters] = useReorderRelativeFiltersMutation();

  const orderedPresets = useMemo(() => sortBy((preset) => preset.position, presets), [presets]);
  const filteredPresets = useMemo(
    () => filterPresetsBySearch(orderedPresets, searchQuery),
    [orderedPresets, searchQuery]
  );
  const sortablePresetIds = useMemo(
    () => orderedPresets.filter((preset) => !preset.system).map((preset) => preset.id),
    [orderedPresets]
  );
  const isDnDEnabled = !isNotNilOrEmpty(trim(searchQuery ?? ''));

  const selectedPreset = useMemo(
    () => orderedPresets.find((preset) => preset.id === selectedPresetId) ?? null,
    [orderedPresets, selectedPresetId]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const scrollAbortRef = useRef<AbortController | undefined>(undefined);

  useEffect(function abortScrollListenerOnUnmount() {
    return () => scrollAbortRef.current?.abort();
  }, []);

  const handleClose = () => {
    closeDropdown();
    setSearchQuery(null);
    scrollAbortRef.current?.abort();
  };

  const handleOpen = () => {
    openDropdown();
    scrollAbortRef.current = new AbortController();
    window.addEventListener('scroll', handleClose, {signal: scrollAbortRef.current.signal});
  };

  const handleSelectPreset = (presetId: string) => {
    onSelectPreset(presetId);
    handleClose();
  };

  const handleCreatePreset = () => {
    onCreatePreset();
    handleClose();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (isNil(over) || active.id === over.id || isNil(customerId)) {
      return;
    }

    const reorderedPresets = reorderPresetsInList(
      orderedPresets,
      String(active.id),
      String(over.id)
    );

    reorderRelativeFilters({
      customerId,
      ...buildReorderRelativeFiltersBody(reorderedPresets),
    })
      .unwrap()
      .catch(handleApiError);
  };

  const presetRows = filteredPresets.map((preset) => {
    const listItemProps = {
      preset,
      isSelected: preset.id === selectedPresetId,
      onSelect: handleSelectPreset,
      'data-testid': suffixTestId(`preset-${preset.id}`, props),
    };

    if (preset.system) {
      return <PresetListItem key={preset.id} {...listItemProps} isSortable={false} />;
    }

    if (isDnDEnabled) {
      return <SortablePresetListItem key={preset.id} {...listItemProps} />;
    }

    return <PresetListItem key={preset.id} {...listItemProps} isSortable={false} />;
  });

  const listContent = match<[boolean, boolean, boolean], ReactNode>([
    isError,
    isLoading,
    filteredPresets.length > 0,
  ])
    .with([true, Pattern.any, Pattern.any], () => (
      <Box padding={3}>
        <Center>
          <Text size="small" color="secondary">
            {i18n.t('entity.filterForm.savedFilters.fetchError')}
          </Text>
        </Center>
      </Box>
    ))
    .with([false, true, Pattern.any], () => (
      <Box padding={4}>
        <Center>
          <Spinner />
        </Center>
      </Box>
    ))
    .with([false, false, false], () => (
      <Box padding={3}>
        <Center>
          <Text size="small" color="secondary">
            {i18n.t('general.labels.noData')}
          </Text>
        </Center>
      </Box>
    ))
    .otherwise(() => presetRows);

  const triggerLabel = selectedPreset?.name ?? placeholder;
  const isPlaceholder = isNil(selectedPreset);

  return (
    <VStack width="100%">
      <Label data-testid={suffixTestId('label', props)}>{label}</Label>
      <Dropdown
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        hasSearch
        isFocusManagementDisabled
        placement="bottom-start"
        data-testid={suffixTestId('savedFiltersDropdown', props)}
        dropdownControl={
          <TriggerHost>
            <SavedFiltersDropdownTrigger
              label={triggerLabel}
              isPlaceholder={isPlaceholder}
              isOpen={isOpen}
              isLoading={isLoading}
              data-testid={props['data-testid']}
            />
          </TriggerHost>
        }
      >
        <PanelLayout>
          <SavedFiltersDropdownSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={i18n.t('general.labels.searchPlaceholder')}
            data-testid={props['data-testid']}
          />
          <ListRegion>
            {isDnDEnabled ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortablePresetIds} strategy={verticalListSortingStrategy}>
                  {listContent}
                </SortableContext>
              </DndContext>
            ) : (
              listContent
            )}
          </ListRegion>
          <SavedFiltersDropdownFooter
            label={i18n.t('entity.filterForm.savedFilters.createNew')}
            isDisabled={isError}
            onClick={handleCreatePreset}
            data-testid={props['data-testid']}
          />
        </PanelLayout>
      </Dropdown>
    </VStack>
  );
}

const TriggerHost = styled.span`
  display: block;
  width: 100%;
`;

// Header (~48px) + card padding + label + trigger + bottom breathing room
const PANEL_VIEWPORT_OFFSET_PX = 160;

const PanelLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - ${({theme}) => theme.getSize(20)});
  min-width: calc(100vw - ${({theme}) => theme.getSize(20)});
  max-height: calc(100vh - ${PANEL_VIEWPORT_OFFSET_PX}px);
  overflow: hidden;

  @media ${({theme}) => theme.devices.notebook}, ${({theme}) => theme.devices.desktop} {
    width: ${({theme}) => theme.getSize(66)};
    min-width: ${({theme}) => theme.getSize(66)};
  }
`;

const ListRegion = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;
